<template>
  <div class="progress-tracker">
    <div class="book-header">
      <span class="book-title">{{ currentBook?.title || 'Loading...' }}</span>
    </div>
    <ul class="chapter-list">
      <li v-for="chapter in currentBook?.chapters || []" :key="chapter.id">
        <div class="chapter-item">
          <span :class="statusClass(currentBookId, chapter.id)"></span>
          <span class="chapter-title">{{ chapter.title }}</span>
        </div>
        <ul class="lesson-list">
          <li v-for="lesson in chapter.lessons" :key="lesson.id">
            <router-link 
              :to="{ name: 'lesson', params: { bookId: currentBookId, chapterId: chapter.id, lessonId: lesson.id }}"
              class="lesson-item"
              :class="{ 'router-link-active': isCurrentLesson(currentBookId, chapter.id, lesson.id) }"
            >
              <span :class="statusClass(currentBookId, chapter.id, lesson.id)"></span>
              <span>{{ lesson.title }}</span>
            </router-link>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { books, getBook } from '../content/chapters'
import { useRoute } from 'vue-router'
import { computed, ref, onMounted, watch, inject } from 'vue'
import { getLastCompletedLesson, parseLessonKey, isLessonCompleted } from '../utils/progress'

export default {
  name: 'ProgressTracker',
  setup() {
    const route = useRoute()
    const lastCompletedLesson = ref(null)
    // Inject the progress initialization state from App.vue
    const progressInitialized = inject('progressInitialized')
    const logger = inject('logger')

    // Get current book ID from route params
    const currentBookId = computed(() => {
      return parseInt(route.params.bookId) || 1
    })

    // Get current book based on route
    const currentBook = computed(() => {
      return getBook(currentBookId.value)
    })

    const isCurrentLesson = (bookId, chapterId, lessonId) => {
      return parseInt(route.params.bookId) === bookId && 
             parseInt(route.params.chapterId) === chapterId && 
             parseInt(route.params.lessonId) === lessonId
    }

    // Function to update progress display
    const updateProgress = () => {
      // Only update after initialization
      if (!progressInitialized.value) {
        logger.debug('ProgressTracker: Waiting for progress to initialize...');
        return;
      }
      
      // Use the cached value - no API call
      const progress = getLastCompletedLesson();
      lastCompletedLesson.value = progress;
      logger.debug('ProgressTracker: Updated with cached progress:', { progress });
    }

    // Watch for initialization and route changes
    watch(
      progressInitialized,
      (initialized) => {
        if (initialized) {
          updateProgress();
        }
      }
    )

    // Update progress when route changes
    watch(
      () => route.params,
      () => {
        if (progressInitialized.value) {
          updateProgress();
        }
      },
      { deep: true }
    )

    // Status class based on progress and current lesson
    const statusClass = (bookId, chapterId, lessonId) => {
      const currentBookId = parseInt(route.params.bookId)
      const currentChapterId = parseInt(route.params.chapterId)
      const currentLessonId = parseInt(route.params.lessonId)

      if (!lessonId && !chapterId) {
        // This is a book status
        if (lastCompletedLesson.value) {
          const completed = parseLessonKey(lastCompletedLesson.value)
          if (completed && completed.bookId > bookId) {
            return 'status completed'
          }
        }
        return `status ${bookId < currentBookId ? 'completed' : 'pending'}`
      }

      if (!lessonId) {
        // This is a chapter status
        if (lastCompletedLesson.value) {
          const completed = parseLessonKey(lastCompletedLesson.value)
          if (completed && completed.bookId > bookId) {
            return 'status completed'
          }
          if (completed && completed.bookId === bookId && completed.chapterId > chapterId) {
            return 'status completed'
          }
        }
        return `status ${bookId < currentBookId || (bookId === currentBookId && chapterId < currentChapterId) ? 'completed' : 'pending'}`
      }

      // This is a lesson status
      if (lastCompletedLesson.value && isLessonCompleted(bookId, chapterId, lessonId, lastCompletedLesson.value)) {
        return 'status completed'
      } else if (bookId === currentBookId && chapterId === currentChapterId && lessonId === currentLessonId) {
        return 'status current'
      }
      return 'status pending'
    }

    return {
      currentBook,
      currentBookId,
      isCurrentLesson,
      statusClass
    }
  }
}
</script>

<style scoped>
.progress-tracker {
  background: #ffffff;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
  width: 250px;
  border-left: 1px solid #e1e8f5;
}

.book-header {
  padding: 15px 0;
  border-bottom: 2px solid #e1e8f5;
  margin-bottom: 15px;
}

.book-title {
  color: #1a365d;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: default;
}

.chapter-list, .lesson-list {
  list-style-type: none;
  padding-left: 15px;
  margin: 0;
}

.chapter-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: #2c5282;
  font-size: 0.95rem;
  font-weight: 600;
}

.chapter-title {
  cursor: default;
}

.lesson-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #4a5568;
  font-size: 0.9rem;
  padding: 6px 10px;
  margin-bottom: 4px;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.lesson-item:hover {
  color: var(--primary);
  background: #f7fafc;
}

.lesson-item.router-link-active {
  color: var(--primary);
  background: #ebf8ff;
  font-weight: 500;
}

.status {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.completed {
  background: #48bb78;
}

.current {
  background: #4299e1;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
}

.pending {
  background: #e2e8f0;
}
</style>
